import { toast } from 'sonner';

/**
 * BI-5-03 single-copy lock toast. One error surface for every layer a blocked
 * franchise edit can reach the client from:
 *   1. client pre-flight ServiceError code `edit_not_allowed` (PostService /
 *      QuickPostService),
 *   2. a 403 `errorCode: 'edit_not_allowed'` from a BI-5-02 server route
 *      (/api/generate-image, video routes),
 *   3. a raw DB-trigger rejection whose message contains `LOCKED_FIELD`
 *      (social_posts BEFORE UPDATE trigger — never show the raw Postgres text).
 *
 * Copy is sentence-case, no jargon (no "RLS"/"trigger"), destructive/semantic
 * tokens only. The editor stays open so unsaved text is preserved.
 */

const LOCKED_EDIT_CODE = 'edit_not_allowed';
const LOCKED_TRIGGER_MARKER = 'LOCKED_FIELD';

/** True when an error (from any layer) is a franchise-lock rejection. */
export function isLockedEditError(err: unknown): boolean {
  if (!err) return false;
  if (typeof err === 'object') {
    const code = (err as { code?: unknown; errorCode?: unknown }).code;
    const errorCode = (err as { errorCode?: unknown }).errorCode;
    if (code === LOCKED_EDIT_CODE || errorCode === LOCKED_EDIT_CODE) return true;
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.includes(LOCKED_TRIGGER_MARKER)) return true;
  }
  if (typeof err === 'string' && err.includes(LOCKED_TRIGGER_MARKER)) return true;
  return false;
}

/** Show the one lock toast. Optional `description` override for the caption
 *  case (which asks the user to restore the disclaimer). */
export function showLockedEditToast(description?: string): void {
  toast.error('Locked by your brand kit', {
    description:
      description ?? 'This is locked by your corporate brand kit and can’t be changed here.',
  });
}

export const LOCKED_CAPTION_TOAST_DESCRIPTION =
  'Captions must include the corporate disclaimer. Restore it to save.';
